import React, { useState, useEffect } from 'react';
import { FormField, Input, Select, Button } from '../ui';
import { LocationInfo, Country, City } from '../../types';
import { api } from '../../services/api';

interface LocationFormProps {
  data: LocationInfo;
  onChange: (data: Partial<LocationInfo>) => void;
  errors: Record<string, string>;
}

export const LocationForm: React.FC<LocationFormProps> = React.memo(({
  data,
  onChange,
  errors,
}) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [generating, setGenerating] = useState<'street' | 'postal' | null>(null);
  // Which fields were last populated by the auto-generator vs the user.
  // Drives the "Auto-matched" adornment in the input rows.
  const [autoFilled, setAutoFilled] = useState<{ street: boolean; postal: boolean }>({
    street: false,
    postal: false,
  });

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await api.getCountries();
        setCountries(response);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (data.country) {
      const fetchCities = async () => {
        setLoadingCities(true);
        try {
          const response = await api.getCities(data.country);
          setCities(response);
        } catch (error) {
          console.error('Failed to fetch cities:', error);
        } finally {
          setLoadingCities(false);
        }
      };
      fetchCities();
    } else {
      setCities([]);
    }
  }, [data.country]);

  const handleCityChange = async (cityName: string) => {
    const selectedCity = cities.find((c) => c.name === cityName);
    onChange({
      city: cityName,
      region: selectedCity?.region || '',
    });

    if (data.country && cityName) {
      try {
        setGenerating('street');
        const autoData = await api.autoGenerate({
          country: data.country,
          city: cityName,
        });
        onChange({
          city: cityName,
          region: selectedCity?.region || '',
          street: autoData.data.street,
          postalCode: autoData.data.postalCode,
        });
        setAutoFilled({ street: true, postal: true });
      } catch (error) {
        console.error('Failed to auto-generate address:', error);
      } finally {
        setGenerating(null);
      }
    }
  };

  const handleRegenerate = async (field: 'street' | 'postal') => {
    if (!data.country || !data.city) return;

    setGenerating(field);
    try {
      const autoData = await api.autoGenerate({
        country: data.country,
        city: data.city,
      });
      if (field === 'street') {
        onChange({ street: autoData.data.street });
        setAutoFilled((prev) => ({ ...prev, street: true }));
      } else {
        onChange({ postalCode: autoData.data.postalCode });
        setAutoFilled((prev) => ({ ...prev, postal: true }));
      }
    } catch (error) {
      console.error(`Failed to regenerate ${field}:`, error);
    } finally {
      setGenerating(null);
    }
  };

  const countryOptions = countries.map((c) => ({
    value: c.code,
    label: c.name,
  }));

  const cityOptions = cities.map((c) => ({
    value: c.name,
    label: c.region ? `${c.name} (${c.region})` : c.name,
  }));

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <FormField
          label="Country"
          required
          error={errors.country}
        >
          <Select
            options={countryOptions}
            value={data.country}
            onChange={(value) => {
              onChange({ country: value, city: '', street: '', postalCode: '', region: '' });
            }}
            placeholder="Select country"
            searchable
            error={errors.country}
          />
        </FormField>

        <FormField
          label="City"
          required
          error={errors.city}
        >
          <Select
            options={cityOptions}
            value={data.city}
            onChange={handleCityChange}
            placeholder={loadingCities ? 'Loading cities...' : 'Select city'}
            searchable
            disabled={!data.country || loadingCities}
            error={errors.city}
          />
        </FormField>

        <FormField
          label="Street Address"
          required
          error={errors.street}
          htmlFor="street-address"
        >
          <div className="flex gap-2" style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Input
                id="street-address"
                type="text"
                value={data.street}
                onChange={(e) => {
                  onChange({ street: e.target.value });
                  setAutoFilled((prev) => ({ ...prev, street: false }));
                }}
                placeholder="Main Street 123"
                error={errors.street}
                disabled={generating === 'street'}
              />
              {autoFilled.street && !errors.street && (
                <span style={adornStyle} aria-hidden="true">
                  Auto-matched
                </span>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={() => handleRegenerate('street')}
              disabled={!data.country || !data.city || generating !== null}
              loading={generating === 'street'}
              title="Regenerate street address"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4 }}>
                <path d="M3 2v6h6M3 8a9 9 0 0 1 15-3l3 3M21 22v-6h-6M21 16a9 9 0 0 1-15 3l-3-3" />
              </svg>
              Regenerate
            </Button>
          </div>
        </FormField>

        <FormField
          label="Postal Code"
          required
          error={errors.postalCode}
          htmlFor="postal-code"
        >
          <div className="flex gap-2" style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Input
                id="postal-code"
                type="text"
                value={data.postalCode}
                onChange={(e) => {
                  onChange({ postalCode: e.target.value });
                  setAutoFilled((prev) => ({ ...prev, postal: false }));
                }}
                placeholder="10115"
                error={errors.postalCode}
                disabled={generating === 'postal'}
              />
              {autoFilled.postal && !errors.postalCode && (
                <span style={adornStyle} aria-hidden="true">
                  Auto-matched
                </span>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={() => handleRegenerate('postal')}
              disabled={!data.country || !data.city || generating !== null}
              loading={generating === 'postal'}
              title="Regenerate postal code"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4 }}>
                <path d="M3 2v6h6M3 8a9 9 0 0 1 15-3l3 3M21 22v-6h-6M21 16a9 9 0 0 1-15 3l-3-3" />
              </svg>
              Regenerate
            </Button>
          </div>
        </FormField>

        <FormField
          label="Region/State"
          error={errors.region}
          htmlFor="region"
          hint="Auto-filled based on city selection"
        >
          <Input
            id="region"
            type="text"
            value={data.region || ''}
            onChange={(e) => onChange({ region: e.target.value })}
            placeholder="Bavaria"
            error={errors.region}
          />
        </FormField>
      </div>
    </div>
  );
});

LocationForm.displayName = 'LocationForm';

/**
 * "Auto-matched" pill overlaid on the right edge of an auto-populated
 * input. Mirrors the reference's .input-adorn / .adorn pattern.
 */
const adornStyle: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  right: 12,
  transform: 'translateY(-50%)',
  padding: '2px 8px',
  fontFamily: 'var(--font-mono)',
  fontSize: '0.65rem',
  letterSpacing: '0.04em',
  color: 'var(--ink-muted)',
  background: 'var(--bg-sunk)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-sm)',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
};

export default LocationForm;
